/* eslint-disable camelcase */
import React, { PureComponent } from "react";
import Modal from "../../components/OldModal";

type SessionManagerProps = {
  sessionTotalLength: number;
  sessionExpiry?: string | null;
  extendSession: () => void;
  requestLogout: () => void;
};

type SessionManagerState = {
  showWarning: boolean;
  sessionExpiry: string | null;
  timeRemaining: number;
};

let warningTimeout: number;
let expirationTimeout: number;

/* Intervals used when the warning is shown. */
let titleSwapInterval: number;
let timeRemainingInterval: number;

const initialPageTitle = document.title;

/* Number of seconds to show the warning to the user for. */
const warningTime = 20;

const FLASHING_INTERVAL = 0.8;

const clearTimeouts = () => {
  window.clearTimeout(warningTimeout);
  window.clearTimeout(expirationTimeout);
};

const clearIntervals = () => {
  clearInterval(titleSwapInterval);
  clearInterval(timeRemainingInterval);
};

/**
 * A SessionManager to handle client side timeouts.
 * Relies on being provided with the configuration of the total Length of sessions
 * and the actual expiry of the client's session.
 */
export default class SessionManager extends PureComponent<
  SessionManagerProps,
  SessionManagerState
> {
  static defaultProps = {
    sessionExpiry: null
  };

  constructor(props: SessionManagerProps) {
    super(props);

    this.state = {
      showWarning: false,
      sessionExpiry: null,
      timeRemaining: warningTime
    };
  }

  componentDidMount() {
    this.extendSession();
  }

  componentDidUpdate(prevProps: SessionManagerProps) {
    if (prevProps.sessionExpiry !== this.state.sessionExpiry) {
      this.resetTimers();
    }
    if (this.state.timeRemaining === 0) {
      this.logout();
    }
  }

  componentWillUnmount() {
    /* The timeouts and intervals MUST be cleared from the window, so they do not continue to execute.
       Hot module replacement will also cause these to be duplicated if this component is updated and hot-reloaded. */
    clearTimeouts();
    clearIntervals();
  }

  onWarn = () => {
    this.setState({ showWarning: true });

    /* Interval which flashes the page's title. */
    titleSwapInterval = window.setInterval(
      this.changeDocumentTitle,
      1000 * FLASHING_INTERVAL
    );
    timeRemainingInterval = window.setInterval(
      this.countdownTimeRemaining,
      1000
    );
  };

  onWarningHidden = () => {
    this.resetWarningState();

    clearIntervals();

    document.title = initialPageTitle;
  };

  changeDocumentTitle = () => {
    if (document.title === initialPageTitle && !document.hasFocus()) {
      document.title = "Session is about to expire";
    } else {
      document.title = initialPageTitle;
    }
  };

  countdownTimeRemaining = () => {
    if (this.state.timeRemaining > 0) {
      this.setState({ timeRemaining: this.state.timeRemaining - 1 });
    }
  };

  logout = () => {
    this.setState({ showWarning: false });
    this.props.requestLogout();
  };

  resetWarningState = () => {
    clearIntervals();
    this.setState({ showWarning: false, timeRemaining: warningTime });
  };

  extendSession = () => {
    this.resetWarningState();

    /* Extend the session timer, this will result in a new sessionExpiry being set in the store and that new value
       coming back down will trigger a reset of the timeouts. */
    this.props.extendSession();
  };

  /* Reset the session warning and session expiration timers. */
  resetTimers = () => {
    const { sessionTotalLength } = this.props;

    const timeUntilWarn = sessionTotalLength - warningTime * 1000;

    clearTimeouts();

    warningTimeout = window.setTimeout(() => this.onWarn(), timeUntilWarn);
    expirationTimeout = window.setTimeout(
      () => this.logout(),
      sessionTotalLength
    );
  };

  render() {
    const title = "Session Expiring";
    const primaryActionLabel = `Keep Session (${this.state.timeRemaining})`;
    const secondaryActionLabel = "Logout";
    const body1 =
      "Your session is about to expire. Any unsaved changes will be lost.";
    const body2 = "Do you want to keep this session?";

    if (this.state.showWarning) {
      return (
        <Modal
          title={title}
          body1={body1}
          body2={body2}
          primaryActionLabel={primaryActionLabel}
          secondaryActionLabel={secondaryActionLabel}
          primaryAction={this.extendSession}
          secondaryAction={this.logout}
        />
      );
    }

    return null;
  }
}
